import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { IconTypes } from '@eika/icon';
import { LinkCard } from '@eika/link-panel';

import { ActionButtonGroup } from '../../components/shared/ActionButtonGroup';
import Downpayment from '../../components/transaction/Downpayment/Downpayment';
import { useConfig } from '../../defaultConfig';
import { useAccount, useAccountNumber } from '../../fetchHooks/useAccount';
import useFavoriteAccount from '../../fetchHooks/useFavoriteAccount';
import { useLoanDetails } from '../../fetchHooks/useLoans';
import type { AccountNumber } from '../../generated-types';
import useAccountId from '../../hooks/queryparams/useAccountId';
import useTranslation from '../../hooks/useTranslation';
import { loansTrackingIDs } from '../../utils/gtm/trackIDs/loans';

import './LoanViewActions.scss';

const getAvdragsfrihetUrl = (url?: string, accountNumber?: AccountNumber, isLoading = false) => {
    if (!url) return null;
    return isLoading ? url : `${url}?stage=0&loanAccount=${accountNumber?.value}`;
};

const LoanViewActions: React.FC = () => {
    const { featureToggles, editLoanLink, avdragsfrihetLink, loanDetailsLink } = useConfig();
    const { data: favoriteAccountNumber } = useFavoriteAccount();
    const { data: favoriteAccount } = useAccountNumber(favoriteAccountNumber);

    const accountId = useAccountId();
    const { data: loanAccount, isLoading } = useAccount(accountId);
    const hasLoanDetails = loanAccount?.additionalInfo.hasLoanDetails === true;
    const { data: loanDetails } = useLoanDetails(accountId, featureToggles.enableDownpayment && hasLoanDetails);

    const translated = useTranslation();

    const [downpaymentModalOpen, setDownPaymentModalOpen] = useState(false);

    const nonDownpaymentLoans = ['FIXED_LOAN', 'SERIAL_FIXED', 'ANNUITY_FIXED'];
    const allowDownpayment =
        !!loanDetails?.loanCategory && !nonDownpaymentLoans.includes(loanDetails.loanCategory) && hasLoanDetails;

    const hasEditLink = !!editLoanLink && featureToggles.enableEditLoan && !!accountId && hasLoanDetails;
    const hasDetailsLink = Boolean(loanDetailsLink) && Boolean(loanAccount) && hasLoanDetails;

    const actions = useMemo(() => {
        const avdragsfrihetUrl = getAvdragsfrihetUrl(avdragsfrihetLink?.url, loanAccount?.accountNumber, isLoading);
        return [
            ...(featureToggles.enableDownpayment && allowDownpayment
                ? [
                      {
                          children: translated.loans.extraDownpayment,
                          icon: IconTypes.OVERFOERE_36,
                          onClick: () => setDownPaymentModalOpen(true),
                          'data-testid': 'extra-downpayment-button',
                          'data-track-id': loansTrackingIDs.editLoan.id,
                          'data-track-linkname': loansTrackingIDs.downpayment.name,
                      },
                  ]
                : []),
            ...(featureToggles.enableAvdragsfrihet && hasLoanDetails && avdragsfrihetUrl
                ? [
                      {
                          children: translated.applyForAvdragsfrihet,
                          icon: IconTypes.BETALE_36,
                          href: avdragsfrihetUrl,
                          'data-testid': 'avdragsfrihet-button',
                          'data-track-id': loansTrackingIDs.interestFree.id,
                          'data-track-linkname': loansTrackingIDs.interestFree.name,
                      },
                  ]
                : []),
            ...(hasDetailsLink
                ? [
                      {
                          children: translated.more,
                          href: `${loanDetailsLink?.url}?id=${loanAccount.accountId.value}`,
                          icon: IconTypes.MER_36,
                          isSecondary: true,
                          hiddenOnDesktop: true,
                          'data-track-id': loansTrackingIDs.loanOverview,
                          'data-track-linkname': loansTrackingIDs.loanOverview,
                          'data-testid': 'loan-details-link',
                      },
                  ]
                : []),
        ];
    }, [
        avdragsfrihetLink?.url,
        loanAccount?.accountNumber,
        loanAccount?.accountId.value,
        isLoading,
        featureToggles.enableDownpayment,
        featureToggles.enableAvdragsfrihet,
        allowDownpayment,
        translated.loans.extraDownpayment,
        translated.applyForAvdragsfrihet,
        translated.more,
        hasLoanDetails,
        hasDetailsLink,
        loanDetailsLink?.url,
    ]);

    return (
        <>
            {actions.length > 0 && (
                <ActionButtonGroup actions={actions} className="account-view__actions-wrapper loan-view-actions" />
            )}
            <section
                className={classNames('loan-view__links', {
                    'has-actions': actions.length > 0,
                    'has-links': hasDetailsLink,
                    'can-edit': hasEditLink,
                })}
            >
                {hasDetailsLink && (
                    <LinkCard
                        className="loan-view__detail-loan-link-desktop"
                        icon={IconTypes.INFO_DYN}
                        url={`${loanDetailsLink?.url}?id=${loanAccount.accountId.value}`}
                        data-testid="loan-details-link"
                        title={translated.loanDetails.seeDetails}
                        textOne={translated.loanDetails.moreInformationAboutThisLoan}
                    />
                )}
                {hasEditLink && (
                    <LinkCard
                        className="loan-view__edit-loan-link"
                        icon={IconTypes.PENN_DYN}
                        url={`${editLoanLink.url}?id=${accountId.value}`}
                        data-testid="edit-loan-link"
                        data-track-id={loansTrackingIDs.editLoan.id}
                        data-track-linkname={loansTrackingIDs.editLoan.name}
                        title={translated.loanEdit.edit}
                        textOne={translated.loanDetails.editPaymentDateAndPaymentAccount}
                    />
                )}
            </section>
            {hasLoanDetails && (
                <Downpayment
                    open={downpaymentModalOpen}
                    setOpen={setDownPaymentModalOpen}
                    account={favoriteAccount}
                    loanAccount={loanAccount}
                    isLoading={isLoading}
                />
            )}
        </>
    );
};

export default LoanViewActions;
